import React, { useRef, useState, useEffect } from "react";
import {
  fetchGuestRecommendations,
  recordRecommendationHistory,
  saveMyProfile,
} from "../api/client";
import { useLocation, useNavigate } from "react-router-dom";
import {
  saveGuestConversionState,
  saveProfile,
  loadProfile,
  clearProfile,
} from "../utils/storage";
import { getOrCreateGuestId } from "../utils/guestSession";
import { getCurrentUser } from "../utils/auth";
import { BETA_ANALYTICS_EVENTS, trackBetaEvent } from "../utils/analytics";
import {
  MOOD_OPTIONS,
  GENRE_OPTIONS,
  COMMITMENT_OPTIONS,
  COMMITMENT_UI_TO_API,
  COMMITMENT_API_TO_UI,
  VIBE_OPTIONS,
  VIBE_API_TO_UI,
  BOUNDARY_OPTIONS,
  BLOCKED_CONTENT_UI_TO_API,
  CULTURAL_TAG_OPTIONS,
  PLATFORM_GROUPS,
} from "../constants/recommendationOptions";
import Button from "./ui/Button";
import Card from "./ui/Card";
import Chip from "./ui/Chip";
import FormField, { fieldClassName } from "./ui/FormField";
import RecoveryPanel from "./RecoveryPanel";

const LAUGH_OPTIONS = [
  ["No comedy preference — funny isn’t required", "None"],
  ["Dry — deadpan, sarcastic, low-key", "Dry"],
  ["Witty — clever banter and sharp dialogue", "Witty"],
  ["Subtle — light humor woven into the story", "Subtle"],
];

const REPRESENTATION_OPTIONS = CULTURAL_TAG_OPTIONS.filter(
  ([, value]) => value !== "no_pref"
);

function authenticatedProfilePayload(profile) {
  return {
    age: profile.age,
    favorite_genres: profile.favorite_genres,
    favorite_moods: profile.favorite_moods,
    commitment_pref: profile.commitment_pref,
    commitment_flexible: profile.commitment_flexible,
    vibe_pref: profile.vibe_pref,
    laugh_pref: profile.laugh_pref,
    blocked_content: profile.blocked_content,
    cultural_tags: profile.cultural_tags,
    ranking_preference: profile.ranking_preference,
    available_platforms: profile.available_platforms,
  };
}

export default function GuestQuiz() {
  const navigate = useNavigate();
  const location = useLocation();
  const moodSeed = location.state?.moodSeed;
  const [guestId] = useState(() => getOrCreateGuestId());
  const [age, setAge] = useState("");
  const [mood, setMood] = useState([]);
  const [genres, setGenres] = useState([]);
  const [commitment, setCommitment] = useState("");
  const [vibe, setVibe] = useState("");
  const [laughPreference, setLaughPreference] = useState("");
  const [boundaries, setBoundaries] = useState([]);
  const [culturalTags, setCulturalTags] = useState([]);
  const [rankingPreference, setRankingPreference] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [wasRestored, setWasRestored] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const quizStartTracked = useRef(false);

  const togglePlatform = (value) => {
    setPlatforms((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const toggleGenre = (value) => {
    setGenres((prev) =>
      prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]
    );
  };

  const toggleBoundary = (value) => {
    setBoundaries((prev) => {
      if (value === "open") {
        return prev.includes("open") ? [] : ["open"];
      }

      const withoutOpen = prev.filter((b) => b !== "open");
      return withoutOpen.includes(value)
        ? withoutOpen.filter((b) => b !== value)
        : [...withoutOpen, value];
    });
  };

  const toggleCulturalTag = (value) => {
    setCulturalTags((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const handleResetQuiz = () => {
    clearProfile();
    setSubmitError(null);
    setAge("");
    setMood([]);
    setGenres([]);
    setCommitment("");
    setVibe("");
    setLaughPreference("");
    setBoundaries([]);
    setCulturalTags([]);
    setRankingPreference("");
    setPlatforms([]);
    setWasRestored(false);
  };

  useEffect(() => {
    if (!quizStartTracked.current) {
      trackBetaEvent(BETA_ANALYTICS_EVENTS.QUIZ_STARTED);
      quizStartTracked.current = true;
    }

    const cached = loadProfile();

    if (!cached) {
      if (Array.isArray(moodSeed?.value) && moodSeed.value.length > 0) {
        setMood(moodSeed.value);
      }
      return;
    }

    setWasRestored(true);

    setAge(cached.age ? String(cached.age) : "");
    setMood(Array.isArray(cached.favorite_moods) ? cached.favorite_moods : []);
    setGenres(
      Array.isArray(cached.favorite_genres) ? cached.favorite_genres : []
    );
    setCommitment(
      cached.commitment_flexible
        ? "open-ended"
        : COMMITMENT_API_TO_UI[cached.commitment_pref] || ""
    );
    setVibe(cached.vibe_pref || VIBE_API_TO_UI[cached.laugh_pref] || "");
    setLaughPreference(cached.vibe_pref ? cached.laugh_pref || "" : "");
    setBoundaries(
      (cached.blocked_content || []).length === 0
        ? ["open"]
        : cached.blocked_content
            .map((b) => (b === "subtitles" ? "no_subtitles" : `no_${b}`))
            .filter(Boolean)
    );
    setCulturalTags(
      Array.isArray(cached.cultural_tags)
        ? cached.cultural_tags.filter((tag) => tag !== "no_pref")
        : []
    );
    setRankingPreference(
      cached.ranking_preference === "vibe_over_rep" ? "vibe_over_rep" : ""
    );
    setPlatforms(
      Array.isArray(cached.available_platforms)
        ? cached.available_platforms
        : []
    );
  }, [moodSeed]);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!age || !mood.length || !genres.length || !commitment || !vibe) {
      setSubmitError({
        title: "Finish the required quiz picks",
        message: "Age, energy, genre, commitment, and vibe are needed before recommendations can be generated.",
      });
      return;
    }

    const profile = {
      guest_id: guestId,
      age: parseInt(age, 10),
      favorite_genres: genres,
      favorite_moods: mood,
      commitment_pref:
        commitment === "open-ended" ? null : COMMITMENT_UI_TO_API[commitment],
      commitment_flexible: commitment === "open-ended",
      vibe_pref: vibe,
      laugh_pref:
        laughPreference === "None" ? null : laughPreference || null,
      blocked_content: boundaries.includes("open")
        ? []
        : boundaries
            .filter((b) => b !== "open")
            .map((b) => BLOCKED_CONTENT_UI_TO_API[b])
            .filter(Boolean),
      cultural_tags: culturalTags.filter((tag) => tag !== "no_pref"),
      ranking_preference: rankingPreference || null,
      available_platforms: platforms,
    };

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      saveProfile(profile);

      const currentUser = await getCurrentUser();
      if (currentUser) {
        await saveMyProfile(authenticatedProfilePayload(profile));
      }

      const res = await fetchGuestRecommendations(profile);
      const results = res?.data?.results;

      if (!Array.isArray(results)) {
        console.error("🚨 No valid results returned from backend", res);
        throw new Error("malformed_recommendation_response");
      }

      trackBetaEvent(BETA_ANALYTICS_EVENTS.QUIZ_COMPLETED, {
        authenticated: Boolean(currentUser),
        result_count: results.length,
        restored: wasRestored,
      });
      trackBetaEvent(BETA_ANALYTICS_EVENTS.RECOMMENDATION_GENERATED, {
        authenticated: Boolean(currentUser),
        result_count: results.length,
        source: "quiz",
      });

      if (currentUser) {
        try {
          await recordRecommendationHistory({
            guest_id: profile.guest_id,
            profile_context: authenticatedProfilePayload(profile),
            results: results.map((show) => ({
              show_id: show.id || null,
              title: show.title || null,
            })),
          });
        } catch (historyError) {
          console.error("Could not save recommendation history:", historyError);
        }
      } else {
        saveGuestConversionState({ profile, results });
      }

      const navigationProfile = currentUser?.id
        ? { ...profile, user_id: currentUser.id }
        : profile;

      navigate("/recommendations", {
        state: {
          results,
          userProfile: navigationProfile,
        },
      });
    } catch (err) {
      console.error("❌ Error fetching guest recommendations:", err);
      const malformed = err.message === "malformed_recommendation_response";
      setSubmitError({
        title: malformed
          ? "Recommendations could not be read"
          : "Recommendations could not be generated",
        message: malformed
          ? "The server responded, but the results were not in the expected shape."
          : "Your quiz answers are still on this page. Try again when the service catches up.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const moodIsSelected = (value) =>
    JSON.stringify(mood) === JSON.stringify(value);

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-6 lg:grid-cols-[220px_1fr_220px]">
        <Card className="hidden p-5 lg:block">
          <p className="text-xs font-bold uppercase text-hookd-gold">Vibe Journey</p>
          <ol className="mt-5 space-y-4 text-sm text-hookd-muted">
            {["Energy", "Genres", "Commitment", "Vibe Level", "Boundaries"].map((step, index) => (
              <li key={step} className="flex items-center gap-3">
                <span className={`flex h-8 w-8 items-center justify-center rounded-full border ${index === 0 ? "border-hookd-empress bg-hookd-purple text-white" : "border-white/20"}`}>
                  {index + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </Card>

        <Card className="p-5 sm:p-8" glow="purple">
          <div className="text-center">
            <p className="text-sm font-bold uppercase text-hookd-empress">Step 1 of 5</p>
            <div className="mx-auto mt-4 flex max-w-md items-center justify-between gap-2">
              {[0, 1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  className={`h-3 flex-1 rounded-full ${step === 0 ? "bg-hookd-purple shadow-glow" : "bg-white/15"}`}
                />
              ))}
            </div>
            <h1 className="mt-8 font-display text-4xl font-bold leading-tight text-hookd-cream sm:text-5xl">
              What kind of energy are you bringing right now?
            </h1>
            <div className="hookd-divider mx-auto my-5 max-w-xs">♕</div>
            <p className="text-hookd-muted">
              Tell me what your spirit's calling for. No wrong answers.
            </p>
          </div>

      {wasRestored && (
            <div className="mt-6 rounded-hookd border border-hookd-gold/35 bg-hookd-gold/10 p-4 text-sm text-hookd-cream">
              You've taken this quiz before. Your last answers are restored, and you can edit or reset below.
            </div>
      )}

      {submitError && (
            <div className="mt-6">
          <RecoveryPanel
            title={submitError.title}
            message={submitError.message}
            actions={[
              {
                label: "Try Again",
                onClick: handleSubmit,
                disabled: isSubmitting,
              },
              {
                label: "Reset Quiz",
                onClick: handleResetQuiz,
              },
            ]}
          />
        </div>
      )}

          <div className="mt-8 space-y-8">
      <div>
              <FormField
                label="How old are you?"
                hint="Just need your age so the recommendations stay age-appropriate."
              >
        <input
          type="number"
          value={age}
          onChange={(e) => setAge(e.target.value)}
                  className={fieldClassName}
          min="1"
        />
              </FormField>
      </div>

            <section>
              <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">Energy</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {MOOD_OPTIONS.map(({ label, value }) => (
          <button
            key={label}
            type="button"
            aria-pressed={moodIsSelected(value)}
            className={`min-h-32 rounded-hookd border p-5 text-left transition ${
              moodIsSelected(value)
                ? "border-hookd-empress bg-hookd-empress/20 text-white shadow-glow"
                : "border-hookd-line bg-white/5 text-hookd-cream hover:border-hookd-empress/60"
            }`}
            onClick={() => setMood(value)}
          >
                    <span className="mb-3 block text-3xl text-hookd-empress">✧</span>
                    <span className="text-lg font-bold">{label}</span>
                    <span className="mt-2 block text-sm text-hookd-muted">
                      {value.join(", ")}
                    </span>
                    {moodIsSelected(value) && (
                      <span className="mt-3 inline-flex rounded-full bg-hookd-purple px-3 py-1 text-xs font-bold uppercase text-white">
                        Selected
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                Stories that usually hook you
              </h2>
              <div className="flex flex-wrap gap-2">
                {GENRE_OPTIONS.map(([label, value]) => (
                  <Chip
                    key={value}
                    as="button"
                    type="button"
                    active={genres.includes(value)}
                    onClick={() => toggleGenre(value)}
                  >
            {label}
                  </Chip>
        ))}
      </div>
            </section>

            <section className="grid gap-5 md:grid-cols-2">
              <div>
                <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                  Commitment
                </h2>
                <div className="grid gap-2">
                  {COMMITMENT_OPTIONS.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={commitment === value}
                      onClick={() => setCommitment(value)}
                      className="justify-start"
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                  Vibe level
                </h2>
                <div className="grid gap-2">
                  {VIBE_OPTIONS.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={vibe === value}
                      onClick={() => setVibe(value)}
                      className="justify-start"
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
            </section>

            <section className="grid gap-5 md:grid-cols-2">
              <div>
                <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                  Laugh preference
                </h2>
                <div className="grid gap-2">
                  {LAUGH_OPTIONS.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={laughPreference === value}
                      onClick={() => setLaughPreference(value)}
                      className="justify-start"
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                  Content boundaries
                </h2>
                <div className="flex flex-wrap gap-2">
                  {BOUNDARY_OPTIONS.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={boundaries.includes(value)}
                      onClick={() => toggleBoundary(value)}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                Representation
              </h2>
              <div className="flex flex-wrap gap-2">
                {REPRESENTATION_OPTIONS.map(([label, value]) => (
                  <Chip
                    key={value}
                    as="button"
                    type="button"
                    active={culturalTags.includes(value)}
                    onClick={() => toggleCulturalTag(value)}
                  >
                    {label}
                  </Chip>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                Ranking balance
              </h2>
              <div className="grid gap-2 md:grid-cols-2">
                {[
                  ["Keep representation and vibe balanced", ""],
                  ["Prioritize the vibe match a little more", "vibe_over_rep"],
                ].map(([label, value]) => (
                  <Chip
                    key={label}
                    as="button"
                    type="button"
                    active={rankingPreference === value}
                    onClick={() => setRankingPreference(value)}
                    className="justify-start"
                  >
                    {label}
                  </Chip>
                ))}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">
                Streaming apps you use
              </h2>
              <div className="space-y-4">
                {Object.entries(PLATFORM_GROUPS).map(([groupLabel, groupPlatforms]) => (
                  <div key={groupLabel}>
                    <p className="mb-2 text-xs font-bold uppercase text-hookd-muted">{groupLabel}</p>
                    <div className="flex flex-wrap gap-2">
                      {groupPlatforms.map((platform) => (
                        <Chip
                          key={platform}
                          as="button"
                          type="button"
                          active={platforms.includes(platform)}
                          onClick={() => togglePlatform(platform)}
                        >
                          {platform}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <Button
        type="button"
        onClick={handleSubmit}
        disabled={isSubmitting}
              loading={isSubmitting}
              className="w-full"
              size="lg"
      >
        {isSubmitting ? "Finding your matches..." : "👑 Show Me The Recs"}
            </Button>

            <Button
        type="button"
        onClick={handleResetQuiz}
              variant="ghost"
              className="w-full"
      >
              Reset Quiz
            </Button>
          </div>
        </Card>

        <div className="grid gap-5">
          <Card className="p-5">
            <p className="text-xs font-bold uppercase text-hookd-gold">The Empress Promise</p>
            <p className="mt-4 font-display text-xl italic text-hookd-cream">
              "You give me your vibe, I'll give you your next obsession."
            </p>
            <p className="mt-4 text-right text-hookd-empress">The Empress</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs font-bold uppercase text-hookd-gold">Need a shortcut?</p>
            <p className="mt-3 text-sm leading-6 text-hookd-muted">
              If you already took the quiz, Guest Mode reloads your saved profile.
            </p>
            <Button variant="secondary" className="mt-5 w-full" onClick={() => navigate("/guest-mode")}>
              Guest Mode
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
